/**
 * Named cubic-bezier easing curves (ease-in / ease-out / ease-in-out families).
 * Source: https://gist.github.com/bendc/ac03faac0bf2aee25b49e5fd260a727d
 *
 * Values are Motion-compatible `[x1, y1, x2, y2]` tuples.
 */

export type CubicBezier = readonly [number, number, number, number];

export const easingCurves = {
  easeInCirc: [0.6, 0.04, 0.98, 0.335],
  easeInCubic: [0.55, 0.055, 0.675, 0.19],
  easeInExpo: [0.95, 0.05, 0.795, 0.035],
  easeInOutCirc: [0.785, 0.135, 0.15, 0.86],
  easeInOutCubic: [0.645, 0.045, 0.355, 1],
  easeInOutExpo: [1, 0, 0, 1],
  easeInOutQuad: [0.455, 0.03, 0.515, 0.955],
  easeInOutQuart: [0.77, 0, 0.175, 1],
  easeInOutQuint: [0.86, 0, 0.07, 1],
  easeInQuad: [0.55, 0.085, 0.68, 0.53],
  easeInQuart: [0.895, 0.03, 0.685, 0.22],
  easeInQuint: [0.755, 0.05, 0.855, 0.06],
  easeOutCirc: [0.075, 0.82, 0.165, 1],
  easeOutCubic: [0.215, 0.61, 0.355, 1],
  easeOutExpo: [0.19, 1, 0.22, 1],
  easeOutQuad: [0.25, 0.46, 0.45, 0.94],
  easeOutQuart: [0.165, 0.84, 0.44, 1],
  easeOutQuint: [0.23, 1, 0.32, 1],
} as const satisfies Record<string, CubicBezier>;

export type EasingCurveName = keyof typeof easingCurves;

export const easingCurveNames = Object.keys(easingCurves) as Array<EasingCurveName>;

export const easingCurveSelectOptions = [
  { label: "Custom (DialKit curve)", value: "custom" },
  ...easingCurveNames.map((name) => ({
    label: name
      .replaceAll(/([A-Z])/g, " $1")
      .replace(/^./, (char) => char.toUpperCase())
      .trim(),
    value: name,
  })),
] as const;

/** Reward stagger v1 custom ease (DialKit export). */
export const defaultRewardStaggerEase: CubicBezier = [0, 1.42, 0.22, 1];

export function resolveEasingCurve(
  preset: string,
  customEase: CubicBezier | [number, number, number, number],
): CubicBezier {
  if (preset === "custom") {
    return customEase;
  }

  if (preset in easingCurves) {
    return easingCurves[preset as EasingCurveName];
  }

  return customEase;
}
